import { useEffect, useState } from "react";
import type { EmergencyCard, Me } from "../../shared/types";
import { api } from "../api";
import { navigate } from "../router";
import { conditionName, speciesEmoji } from "../format";

export function EmergencyView({ me, chip }: { me: Me; chip: string }) {
  const [card, setCard] = useState<EmergencyCard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.emergency(chip).then(setCard, (e) => setError(e.message)); }, [chip]);

  if (me.role !== "vet") return <main className="container"><div className="err">Emergency lookup is available to clinic staff only.</div></main>;
  if (error) return <main className="container"><div className="err mb">{error}</div><button className="btn ghost" onClick={() => navigate("/")}>← Back</button></main>;
  if (!card) return <div className="center"><div className="spinner" />Scanning chip {chip}…</div>;

  const { pet, allergies } = card;
  return (
    <main className="container" style={{ maxWidth: 820 }}>
      <div className="er-head">
        <div style={{ fontSize: 24 }}>⚡</div>
        <div><b>Emergency view</b><br /><span>Critical information only · {card.verified ? "🔗 chain verified" : "⚠ chain verification failed"}</span></div>
        <button className="btn sm" style={{ marginLeft: "auto", background: "rgba(255,255,255,.18)" }} onClick={() => navigate(`/pets/${pet.id}`)}>Open full record ›</button>
      </div>
      <div className="top-bar">
        {pet.photoUrl ? <img className="pat-photo" src={pet.photoUrl} alt={pet.name} /> : <div className="pat-photo">{speciesEmoji(pet.species)}</div>}
        <div>
          <div className="pn">{pet.displayName}</div>
          <div className="ps">{[pet.species, pet.breed, pet.sex, pet.ageLabel, pet.weightLb && `${pet.weightLb} lb`].filter(Boolean).join(" · ")}</div>
          <div className="pid">Microchip: {pet.microchip}</div>
        </div>
        <div className="top-meta" style={{ marginLeft: "auto" }}>
          <div className="a">Owner: {card.ownerPhone ?? "—"}</div>
          <div className="b">{card.lastClinic ? `${card.lastClinic.name} · ${card.lastClinic.phone ?? ""}` : ""}</div>
        </div>
      </div>
      <div className="pn-wrap" style={{ borderRadius: "0 0 12px 12px", border: "0.5px solid var(--bd)", borderTop: "none" }}>
        <div className="allergy-block mb">
          <div className="allergy-drug">
            <div className="all-icon all-icon-red">⛔</div>
            <div><div className="all-label all-label-red">Drug allergies — do not administer</div>
              <div className="atags">{allergies.drug.length ? allergies.drug.map((a) => <span key={a} className="atag-red" style={{ fontSize: 14.5 }}>{a}</span>) : <span className="t2">None recorded</span>}</div></div>
          </div>
          {allergies.medical.length > 0 && (
            <div className="allergy-other">
              <div className="all-icon all-icon-med">≈</div>
              <div><div className="all-label all-label-med">Medical sensitivities</div><div className="atags">{allergies.medical.map((a) => <span key={a} className="atag-med">{a}</span>)}</div></div>
            </div>
          )}
        </div>
        <div className="g2">
          <div className="card">
            <div className="ch"><div className="ct">Active conditions</div></div>
            {card.conditions.length ? card.conditions.map((c) => (
              <div key={c.name} className="row"><div className="ico" style={{ background: "var(--wl)" }}>{c.icon ?? "•"}</div><div><div className="t1">{conditionName(c.name)} <span className="t3">· {c.status}</span></div><div className="t2">{c.detail}</div></div></div>
            )) : <div className="row t2">None on record.</div>}
          </div>
          <div className="card">
            <div className="ch"><div className="ct">Current medications &amp; diet</div></div>
            {card.medications.map((m) => (
              <div key={m.id} className="row"><div style={{ fontSize: 16.5 }}>{m.icon ?? "💊"}</div><div><div className="t1">{m.name}</div><div className="t2">{m.instructions}</div></div></div>
            ))}
            {card.externalMeds.map((m) => (
              <div key={m.name} className="row"><div style={{ fontSize: 16.5 }}>💊</div><div><div className="t1">{m.name} <span className="src-pill">{m.source === "owner" ? "owner" : "other clinic"}</span></div><div className="t2">{[m.instructions, m.quantity && `Qty ${m.quantity}`].filter(Boolean).join(" · ")}</div></div></div>
            ))}
            {!card.medications.length && !card.externalMeds.length && <div className="row t2">None.</div>}
          </div>
        </div>
        {card.cautions.length > 0 && (
          <div className="card">
            <div className="ch"><div className="ct">Handling cautions</div><div className="cb bw">Watch</div></div>
            {card.cautions.map((b) => <div key={b.id} className="row" style={{ fontSize: 13 }}>⚠ <span><b>{b.headline}</b>{b.headline ? " — " : ""}{b.body}</span></div>)}
          </div>
        )}
      </div>
    </main>
  );
}
